import { Box, Popover, SxProps, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useState } from 'react'
import { Variant } from '@mui/material/styles/createTypography'
import { GuiLiveContentPosition, GuiLiveItemPosition, LiveViewBadge } from 'src/Libs/live-view'
import { LiveItemType } from 'src/Elements/LiveView/types'
import { DASHBOARD_GUI_LIVE_VIEW_VALUE_PREFIX, DASHBOARD_LIVE_VIEW_ITEM_PREFIX } from 'src/Libs/qa/ids'
import { GUI_LIVE_VIEW_BREAKPOINT_360, GUI_LIVE_VIEW_BREAKPOINT_XS } from 'src/Libs/live-view/utils/breakpoints'

const LABEL_OFFSET = 90
const TITLE_RESERVED_HEIGHT_SM_UP = '20px'
const TITLE_RESERVED_HEIGHT_360 = '36px'
const ICON_CONTAINER_SIZE = 60
const MIDDLE_LIVE_ITEM_WIDTH = '140px'

interface Props {
  icon: React.ReactNode
  label: string
  value: string
  labelVariant: Variant
  valueVariant: Variant
  badgeAmount?: number
  popoverContent?: React.ReactNode
  showDynamicPrices: boolean
  children?: React.ReactNode
  position: GuiLiveItemPosition
  sx: SxProps
  contentSx?: SxProps
  iconClassName?: string
  iconPosition: GuiLiveContentPosition
  animationComponent: React.ReactNode
  overlayIcon?: React.ReactNode
  type: LiveItemType
  id: string
  isLastRow?: boolean
}

const GuiLiveViewItem: React.FC<Props> = ({
  icon,
  label,
  value,
  children,
  labelVariant,
  valueVariant,
  badgeAmount,
  popoverContent,
  position,
  contentSx,
  sx,
  iconClassName,
  iconPosition,
  animationComponent,
  overlayIcon,
  type,
  id,
  isLastRow = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isTopElement = position.includes('top')
  const theme = useTheme()
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'))
  const isBelow360 = useMediaQuery(theme.breakpoints.down(GUI_LIVE_VIEW_BREAKPOINT_360))
  const isBetween360AndXs = useMediaQuery(theme.breakpoints.between(GUI_LIVE_VIEW_BREAKPOINT_360, GUI_LIVE_VIEW_BREAKPOINT_XS))

  const popoverIsOpen = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => (popoverContent ? setAnchorEl(event.currentTarget) : undefined)
  const handleClose = (): void => setAnchorEl(null)

  const iconComponent = <Box sx={{ width: '100%', height: '100%' }}>{icon}</Box>
  const valueComponent = (
    <Typography
      variant={valueVariant}
      align="center"
      data-qa={`${DASHBOARD_GUI_LIVE_VIEW_VALUE_PREFIX}${type}`}
      sx={theme => ({
        [theme.breakpoints.down('sm')]: {
          maxWidth: ICON_CONTAINER_SIZE + 10,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      })}
    >
      {value}
    </Typography>
  )

  const getTitleComponentWidth = (): string | number => {
    if (type === LiveItemType.evsesSub && !isBelow360) {
      return MIDDLE_LIVE_ITEM_WIDTH
    }

    if (isSmDown) return ICON_CONTAINER_SIZE + 30

    return ICON_CONTAINER_SIZE + 50
  }

  const titleComponentSx = {
    width: getTitleComponentWidth(),
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
  }

  const titleComponent = (
    <Typography
      variant={labelVariant}
      align="center"
      id={id + '-title'}
      sx={() => ({
        ...titleComponentSx,
        position: 'absolute',
        top: isTopElement ? 'auto' : LABEL_OFFSET,
        bottom: isTopElement ? LABEL_OFFSET : 'auto',
      })}
    >
      {label}
    </Typography>
  )

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        position: 'relative',
        maxWidth: 'max-content',
        height: 'max-content',
        id,
        ...contentSx,
      }}
    >
      {iconPosition === 'top' ? titleComponent : valueComponent}
      <Box
        sx={theme => ({
          width: ICON_CONTAINER_SIZE,
          height: ICON_CONTAINER_SIZE,
          borderWidth: theme.shape.liveViewIconContainer.borderWidth,
          borderStyle: theme.shape.liveViewIconContainer.borderStyle,
          borderColor: theme.shape.liveViewIconContainer.borderColor,
          borderRadius: theme.shape.liveViewIconContainer.borderRadius,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          '&:hover': {
            cursor: popoverContent ? 'pointer' : 'default',
            transform: popoverContent ? 'scale(1.02)' : 'none',
          },
        })}
        className={iconClassName}
        onClick={handleClick}
      >
        <LiveViewBadge overlayIcon={overlayIcon} iconComponent={iconComponent} badgeAmount={badgeAmount ?? 0} />
      </Box>
      {iconPosition === 'top' ? valueComponent : titleComponent}
    </Box>
  )

  return (
    <Box
      sx={[
        {
          position: 'relative',
          maxWidth: 'fit-content',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
        isLastRow && { paddingBottom: isBetween360AndXs ? TITLE_RESERVED_HEIGHT_360 : TITLE_RESERVED_HEIGHT_SM_UP },
        isBetween360AndXs && position.includes('left') ? { transform: 'translateX(6px)' } : {},
        isBetween360AndXs && position.includes('right') ? { transform: 'translateX(-6px)' } : {},
      ]}
      data-qa={`${DASHBOARD_LIVE_VIEW_ITEM_PREFIX}${type}`}
    >
      {iconPosition === 'top' && content}
      {animationComponent}
      {iconPosition === 'bottom' && content}
      {popoverContent && (
        <Popover
          anchorEl={anchorEl}
          open={popoverIsOpen}
          onClose={handleClose}
          disableScrollLock
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ horizontal: 'center', vertical: 'top' }}
        >
          {popoverContent}
        </Popover>
      )}
      {children}
    </Box>
  )
}

export default GuiLiveViewItem
